import type { GeocodeResult, GeocodeStatus, GeoPoint } from '@/features/network/models';

type ProviderCandidate = {
  point: GeoPoint;
  confidence: number | null;
  displayName: string | null;
};

export interface GeocodingProvider {
  name: string;
  isConfigured(): boolean;
  forward(query: string, signal?: AbortSignal): Promise<ProviderCandidate | null>;
}

export type GeocodingConfig = {
  cacheTtlMs?: number;
  cacheVersion?: number;
  minRequestIntervalMs?: number;
  primaryProvider?: GeocodingProvider;
  secondaryProvider?: GeocodingProvider;
  allowSecondaryWithoutPrimary?: boolean;
};

const DEFAULT_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14;
const DEFAULT_CACHE_VERSION = 1;
const DEFAULT_MIN_REQUEST_INTERVAL_MS = 250;
const DEFAULT_RATE_LIMIT_COOLDOWN_MS = 1000 * 90;
const SECONDARY_ONLY_FALLBACK_COOLDOWN_MS = 1000 * 60;
const NOMINATIM_MIN_REQUEST_INTERVAL_MS = 1000;
const NOMINATIM_MAX_RETRIES = 1;
const NOMINATIM_DEFAULT_BACKOFF_MS = 1250;
const DEV_NOMINATIM_BASE = '/api/nominatim';
const STORAGE_KEY = 'geo:forward-geocode-cache';

type CacheRecord = {
  version: number;
  expiresAt: number;
  value: GeocodeResult;
};

class BasicRateLimiter {
  private nextAvailableAt = 0;

  async wait(minIntervalMs: number): Promise<void> {
    const now = Date.now();
    const waitMs = Math.max(0, this.nextAvailableAt - now);
    this.nextAvailableAt = Math.max(this.nextAvailableAt, now) + minIntervalMs;

    if (waitMs <= 0) {
      return;
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, waitMs);
    });
  }
}

class ProviderHttpError extends Error {
  constructor(
    readonly provider: string,
    readonly status: number,
    message?: string,
  ) {
    super(message ?? `${provider} HTTP ${status}`);
    this.name = 'ProviderHttpError';
  }
}

class ProviderUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProviderUnavailableError';
  }
}

const rateLimiter = new BasicRateLimiter();
const memoryCache = new Map<string, CacheRecord>();
const inFlightRequests = new Map<string, Promise<GeocodeResult>>();
const rateLimitCooldownCache = new Map<string, CacheRecord>();
let secondaryOnlyFallbackNextAllowedAt = 0;

export function hasGeoapifyApiKeyConfigured(): boolean {
  const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY as string | undefined;
  return Boolean(apiKey?.trim());
}

function normalizeQuery(input: string): string {
  return input.trim().replace(/\s+/g, ' ').toLowerCase();
}

function isStorageAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readStorageCache(): Record<string, CacheRecord> {
  if (!isStorageAvailable()) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    return parsed as Record<string, CacheRecord>;
  } catch {
    return {};
  }
}

function writeStorageCache(entries: Record<string, CacheRecord>): void {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // ignore quota / privacy errors
  }
}

function getCachedResult(key: string, version: number): GeocodeResult | null {
  const now = Date.now();

  const memoryEntry = memoryCache.get(key);
  if (memoryEntry && memoryEntry.version === version && memoryEntry.expiresAt > now) {
    return { ...memoryEntry.value, cached: true };
  }

  const storageCache = readStorageCache();
  const storageEntry = storageCache[key];
  if (!storageEntry) {
    return null;
  }

  if (storageEntry.version !== version || storageEntry.expiresAt <= now) {
    delete storageCache[key];
    writeStorageCache(storageCache);
    return null;
  }

  memoryCache.set(key, storageEntry);
  return { ...storageEntry.value, cached: true };
}

function setCachedResult(key: string, value: GeocodeResult, ttlMs: number, version: number): void {
  const entry: CacheRecord = {
    version,
    expiresAt: Date.now() + ttlMs,
    value: { ...value, cached: false },
  };

  memoryCache.set(key, entry);

  const storageCache = readStorageCache();
  storageCache[key] = entry;
  writeStorageCache(storageCache);
}

function withAbortSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) {
    return promise;
  }

  if (signal.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const cleanup = () => {
      signal.removeEventListener('abort', onAbort);
    };

    signal.addEventListener('abort', onAbort, { once: true });

    void promise.then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (error) => {
        cleanup();
        reject(error);
      },
    );
  });
}

function getRateLimitCooldownResult(key: string, version: number): GeocodeResult | null {
  const now = Date.now();
  const memoryEntry = rateLimitCooldownCache.get(key);
  if (memoryEntry && memoryEntry.version === version && memoryEntry.expiresAt > now) {
    return { ...memoryEntry.value, cached: true };
  }

  const cached = getCachedResult(key, version);
  if (cached && cached.status === 'rate_limited') {
    return cached;
  }

  if (memoryEntry && memoryEntry.expiresAt <= now) {
    rateLimitCooldownCache.delete(key);
  }

  return null;
}

function setRateLimitCooldownResult(key: string, value: GeocodeResult, cooldownMs: number, version: number): void {
  const entry: CacheRecord = {
    version,
    expiresAt: Date.now() + cooldownMs,
    value: { ...value, cached: false },
  };

  rateLimitCooldownCache.set(key, entry);
  setCachedResult(key, value, cooldownMs, version);
}

export function getForwardGeocodeTaskState(
  rawQuery: string,
  config: GeocodingConfig = {},
): 'ready' | 'in_flight' | 'cooldown' {
  const normalizedQuery = normalizeQuery(rawQuery);
  if (!normalizedQuery) {
    return 'ready';
  }

  const cacheVersion = config.cacheVersion ?? DEFAULT_CACHE_VERSION;
  const cacheKey = `v${cacheVersion}:${normalizedQuery}`;

  if (inFlightRequests.has(cacheKey)) {
    return 'in_flight';
  }

  return getRateLimitCooldownResult(cacheKey, cacheVersion) ? 'cooldown' : 'ready';
}

function makeResult(params: {
  query: string;
  normalizedQuery: string;
  status: GeocodeStatus;
  point?: GeoPoint | null;
  confidence?: number | null;
  provider?: string | null;
  displayName?: string | null;
  cached?: boolean;
  error?: string | null;
}): GeocodeResult {
  return {
    query: params.query,
    normalizedQuery: params.normalizedQuery,
    status: params.status,
    point: params.point ?? null,
    confidence: params.confidence ?? null,
    provider: params.provider ?? null,
    displayName: params.displayName ?? null,
    cached: params.cached ?? false,
    error: params.error ?? null,
  };
}

function mapErrorToStatus(error: unknown): GeocodeStatus {
  if (error instanceof ProviderUnavailableError) {
    return 'unavailable';
  }

  if (error instanceof ProviderHttpError && error.status === 429) {
    return 'rate_limited';
  }

  if (error instanceof ProviderHttpError) {
    return 'provider_error';
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'network_error';
  }

  if (error instanceof TypeError) {
    return 'network_error';
  }

  return 'provider_error';
}

export class GeoapifyProvider implements GeocodingProvider {
  readonly name = 'geoapify';

  constructor(private readonly apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY as string | undefined) {}

  isConfigured(): boolean {
    return Boolean(this.apiKey?.trim());
  }

  async forward(query: string, signal?: AbortSignal): Promise<ProviderCandidate | null> {
    if (!this.apiKey) {
      return null;
    }

    const url = new URL('https://api.geoapify.com/v1/geocode/search');
    url.searchParams.set('text', query);
    url.searchParams.set('limit', '1');
    url.searchParams.set('format', 'json');
    url.searchParams.set('apiKey', this.apiKey);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) {
      throw new Error(`Geoapify HTTP ${response.status}`);
    }

    const data = (await response.json()) as {
      results?: Array<{ lat?: number; lon?: number; formatted?: string; rank?: { confidence?: number } }>;
    };
    const first = data.results?.[0];
    if (!first || typeof first.lat !== 'number' || typeof first.lon !== 'number') {
      return null;
    }

    return {
      point: { lat: first.lat, lon: first.lon },
      confidence: typeof first.rank?.confidence === 'number' ? first.rank.confidence : null,
      displayName: first.formatted ?? null,
    };
  }
}

export class NominatimProvider implements GeocodingProvider {
  readonly name = 'nominatim';
  private static readonly rateLimiter = new BasicRateLimiter();

  private resolveBaseUrl(): string | null {
    if (import.meta.env.DEV) {
      return DEV_NOMINATIM_BASE;
    }

    const configuredProxy = (import.meta.env.VITE_NOMINATIM_PROXY_BASE_URL as string | undefined)?.trim();
    if (configuredProxy) {
      return configuredProxy;
    }

    return null;
  }

  private buildSearchUrl(query: string): string | null {
    const baseUrl = this.resolveBaseUrl();
    if (!baseUrl) {
      return null;
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = new URL(`${normalizedBase}/search`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('limit', '1');
    return url.toString();
  }

  private parseRetryAfterMs(response: Response): number {
    const retryAfterHeader = response.headers.get('Retry-After');
    if (!retryAfterHeader) {
      return NOMINATIM_DEFAULT_BACKOFF_MS;
    }

    const seconds = Number(retryAfterHeader);
    if (!Number.isNaN(seconds)) {
      return Math.max(NOMINATIM_DEFAULT_BACKOFF_MS, Math.ceil(seconds * 1000));
    }

    const retryAt = Date.parse(retryAfterHeader);
    if (Number.isNaN(retryAt)) {
      return NOMINATIM_DEFAULT_BACKOFF_MS;
    }

    return Math.max(NOMINATIM_DEFAULT_BACKOFF_MS, retryAt - Date.now());
  }

  private waitMs(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        cleanup();
        resolve();
      }, ms);

      const onAbort = () => {
        window.clearTimeout(timer);
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      };

      const cleanup = () => {
        signal?.removeEventListener('abort', onAbort);
      };

      if (signal?.aborted) {
        onAbort();
        return;
      }

      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  isConfigured(): boolean {
    return this.resolveBaseUrl() !== null;
  }

  async forward(query: string, signal?: AbortSignal): Promise<ProviderCandidate | null> {
    const searchUrl = this.buildSearchUrl(query);
    if (!searchUrl) {
      throw new ProviderUnavailableError(
        'Nominatim browser requests are disabled in production. Configure VITE_NOMINATIM_PROXY_BASE_URL to your backend geocoding proxy.',
      );
    }

    await NominatimProvider.rateLimiter.wait(NOMINATIM_MIN_REQUEST_INTERVAL_MS);

    let response: Response | null = null;
    for (let attempt = 0; attempt <= NOMINATIM_MAX_RETRIES; attempt += 1) {
      response = await fetch(searchUrl, {
        signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        break;
      }

      if (response.status !== 429 || attempt === NOMINATIM_MAX_RETRIES) {
        throw new ProviderHttpError(this.name, response.status);
      }

      const backoffMs = this.parseRetryAfterMs(response);
      await this.waitMs(backoffMs, signal);
      await NominatimProvider.rateLimiter.wait(NOMINATIM_MIN_REQUEST_INTERVAL_MS);
    }

    if (!response || !response.ok) {
      throw new ProviderHttpError(this.name, response?.status ?? 500);
    }

    const data = (await response.json()) as Array<{ lat?: string; lon?: string; display_name?: string; importance?: number }>;
    const first = data[0];
    if (!first || !first.lat || !first.lon) {
      return null;
    }

    const lat = Number(first.lat);
    const lon = Number(first.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      return null;
    }

    return {
      point: { lat, lon },
      confidence: typeof first.importance === 'number' ? first.importance : null,
      displayName: first.display_name ?? null,
    };
  }
}

export async function forwardGeocode(
  rawQuery: string,
  config: GeocodingConfig = {},
  signal?: AbortSignal,
): Promise<GeocodeResult> {
  const normalizedQuery = normalizeQuery(rawQuery);
  const cacheVersion = config.cacheVersion ?? DEFAULT_CACHE_VERSION;
  const cacheTtlMs = config.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS;
  const minRequestIntervalMs = config.minRequestIntervalMs ?? DEFAULT_MIN_REQUEST_INTERVAL_MS;
  const rateLimitCooldownMs = DEFAULT_RATE_LIMIT_COOLDOWN_MS;

  if (!normalizedQuery) {
    return makeResult({
      query: rawQuery,
      normalizedQuery,
      status: 'invalid_input',
      error: 'Query is empty after normalization',
    });
  }

  const cacheKey = `v${cacheVersion}:${normalizedQuery}`;
  const cached = getCachedResult(cacheKey, cacheVersion);
  if (cached) {
    return cached;
  }

  const cooldownCached = getRateLimitCooldownResult(cacheKey, cacheVersion);
  if (cooldownCached) {
    return cooldownCached;
  }

  const existingPromise = inFlightRequests.get(cacheKey);
  if (existingPromise) {
    return withAbortSignal(existingPromise, signal);
  }

  const taskPromise = (async (): Promise<GeocodeResult> => {
    const primaryProvider = config.primaryProvider ?? new GeoapifyProvider();
    const secondaryProvider = config.secondaryProvider ?? new NominatimProvider();
    const configuredProviders: GeocodingProvider[] = [];

    if (primaryProvider.isConfigured()) {
      configuredProviders.push(primaryProvider);
    }

    if (secondaryProvider.isConfigured()) {
      configuredProviders.push(secondaryProvider);
    }

    if (configuredProviders.length === 0) {
      const unavailable = makeResult({
        query: rawQuery,
        normalizedQuery,
        status: 'unavailable',
        error: 'No configured geocoding provider',
      });
      setCachedResult(cacheKey, unavailable, cacheTtlMs, cacheVersion);
      return unavailable;
    }

    const isSecondaryOnlyFallbackMode =
      !primaryProvider.isConfigured() &&
      secondaryProvider.isConfigured() &&
      configuredProviders.length === 1 &&
      configuredProviders[0]?.name === secondaryProvider.name;

    if (isSecondaryOnlyFallbackMode && !config.allowSecondaryWithoutPrimary) {
      const now = Date.now();
      if (now < secondaryOnlyFallbackNextAllowedAt) {
        const fallbackThrottled = makeResult({
          query: rawQuery,
          normalizedQuery,
          status: 'rate_limited',
          provider: secondaryProvider.name,
          error: 'Secondary fallback is throttled because primary geocoding provider is not configured',
        });
        setRateLimitCooldownResult(cacheKey, fallbackThrottled, rateLimitCooldownMs, cacheVersion);
        return fallbackThrottled;
      }

      secondaryOnlyFallbackNextAllowedAt = now + SECONDARY_ONLY_FALLBACK_COOLDOWN_MS;
    }

    let lastError: unknown = null;
    let lastProviderName: string | null = null;

    for (const provider of configuredProviders) {
      lastProviderName = provider.name;

      try {
        await rateLimiter.wait(minRequestIntervalMs);
        const candidate = await provider.forward(normalizedQuery);

        if (!candidate) {
          continue;
        }

        const success = makeResult({
          query: rawQuery,
          normalizedQuery,
          status: 'ok',
          point: candidate.point,
          confidence: candidate.confidence,
          provider: provider.name,
          displayName: candidate.displayName,
        });

        setCachedResult(cacheKey, success, cacheTtlMs, cacheVersion);
        return success;
      } catch (error) {
        lastError = error;
      }
    }

    const status = lastError ? mapErrorToStatus(lastError) : 'not_found';
    const failure = makeResult({
      query: rawQuery,
      normalizedQuery,
      status,
      provider: lastProviderName,
      error: lastError instanceof Error ? lastError.message : null,
    });

    if (status === 'rate_limited') {
      setRateLimitCooldownResult(cacheKey, failure, rateLimitCooldownMs, cacheVersion);
      return failure;
    }

    setCachedResult(cacheKey, failure, cacheTtlMs, cacheVersion);
    return failure;
  })().finally(() => {
    inFlightRequests.delete(cacheKey);
  });

  inFlightRequests.set(cacheKey, taskPromise);

  return withAbortSignal(taskPromise, signal);
}
